import { formatINR } from "@/lib/format";

const Metric = ({ label, value, testid, accent }) => (
  <div className="py-3 flex items-baseline justify-between border-b border-slate-100 last:border-none">
    <span className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
      {label}
    </span>
    <span
      className={`font-mono-num text-base ${
        accent === "primary"
          ? "text-blue-800 font-semibold"
          : accent === "success"
          ? "text-emerald-700 font-semibold"
          : "text-slate-900"
      }`}
      data-testid={testid}
    >
      {value}
    </span>
  </div>
);

export const KeyMetricsGrid = ({ summary }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 print-block">
    <MetricCard
      label="Annual Payout"
      value={formatINR(summary.annual)}
      testid="metric-annual-payout"
    />
    <MetricCard
      label="15× Revenue Target"
      value={formatINR(summary.target15)}
      accent="primary"
      testid="metric-target-15x"
    />
    <MetricCard
      label="Expected @ Historical Rate"
      value={formatINR(summary.expectedRev)}
      subtitle={`at ${(summary.historicalRate * 100).toFixed(0)}% achievement`}
      testid="metric-expected-revenue"
    />
    <MetricCard
      label="Break-even (10×)"
      value={formatINR(summary.breakEven)}
      testid="metric-breakeven"
    />
  </div>
);

const MetricCard = ({ label, value, subtitle, accent, testid }) => (
  <div className="border border-slate-200 bg-white p-5 hover:shadow-sm transition-shadow duration-200">
    <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500">
      {label}
    </p>
    <p
      className={`mt-3 font-display text-2xl ${
        accent === "primary" ? "text-blue-800" : "text-slate-900"
      }`}
      data-testid={testid}
    >
      {value}
    </p>
    {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
  </div>
);

export const ScenarioSummary = ({ summary }) => {
  const s1 = summary.scenario1;
  const s2 = summary.scenario2;

  return (
    <section
      className="grid grid-cols-1 lg:grid-cols-2 gap-6 print-block"
      data-testid="scenario-summary-section"
    >
      {/* Scenario 1 */}
      <article
        className="border border-slate-200 bg-white p-8"
        data-testid="scenario-1-card"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500">
              Scenario 1
            </p>
            <h3 className="font-display text-2xl mt-1 text-slate-900">
              Current Pay + Balance Incentive
            </h3>
          </div>
          <span className="pill pill-slate">100% Fixed Base</span>
        </div>

        <div className="mt-4">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">
            Total Payout Pool @ 15× (10%)
          </p>
          <p className="font-display text-3xl text-slate-900 font-mono-num">
            {formatINR(s1.pool_annual)}
          </p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Annual
            </p>
            <Metric
              label="Fixed Payout"
              value={formatINR(s1.fixed_annual)}
              testid="s1-fixed-annual"
            />
            <Metric
              label="Balance Incentive"
              value={formatINR(s1.incentive_annual)}
              testid="s1-incentive-annual"
              accent="success"
            />
            <Metric
              label="Total Earnings"
              value={formatINR(s1.total_annual)}
              testid="s1-total-annual"
              accent="primary"
            />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Monthly
            </p>
            <Metric
              label="Fixed Payout"
              value={formatINR(s1.fixed_monthly)}
              testid="s1-fixed-monthly"
            />
            <Metric
              label="Balance Incentive"
              value={formatINR(s1.incentive_monthly)}
              testid="s1-incentive-monthly"
              accent="success"
            />
            <Metric
              label="Total Earnings"
              value={formatINR(s1.total_monthly)}
              testid="s1-total-monthly"
              accent="primary"
            />
          </div>
        </div>
      </article>

      {/* Scenario 2 */}
      <article
        className="border border-slate-200 bg-white p-8"
        data-testid="scenario-2-card"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500">
              Scenario 2
            </p>
            <h3 className="font-display text-2xl mt-1 text-slate-900">
              Counter Offer — 70/30 Model
            </h3>
          </div>
          <span className="pill pill-slate">70% Fixed · 30% Variable</span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">
              Revenue Benchmark 11×
            </p>
            <p className="font-mono-num text-lg font-semibold text-slate-900" data-testid="s2-target-11x">
              {formatINR(summary.target11)}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">
              Payout Pool @ 11× (10%)
            </p>
            <p className="font-mono-num text-lg font-semibold text-slate-900" data-testid="s2-pool-11x">
              {formatINR(s2.pool_11x)}
            </p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Annual
            </p>
            <Metric
              label="Fixed (70%)"
              value={formatINR(s2.fixed_annual)}
              testid="s2-fixed-annual"
            />
            <Metric
              label="Incentive @ 11×"
              value={formatINR(s2.incentive_11x_annual)}
              testid="s2-incentive-11x-annual"
              accent="success"
            />
            <Metric
              label="Variable @ 15× (30%)"
              value={formatINR(s2.variable_annual)}
              testid="s2-variable-annual"
              accent="success"
            />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Monthly
            </p>
            <Metric
              label="Fixed"
              value={formatINR(s2.fixed_monthly)}
              testid="s2-fixed-monthly"
            />
            <Metric
              label="Incentive @ 11×"
              value={formatINR(s2.incentive_monthly_11x)}
              testid="s2-incentive-monthly"
              accent="success"
            />
            <Metric
              label="Variable @ 15×"
              value={formatINR(s2.variable_monthly)}
              testid="s2-variable-monthly"
              accent="success"
            />
          </div>
        </div>

        <div className="mt-6 border-t border-slate-200 pt-4 grid grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Total @ 11× Achievement
            </p>
            <p
              className="font-display text-2xl mt-1 text-blue-800 font-mono-num"
              data-testid="s2-total-at-11x"
            >
              {formatINR(s2.total_at_11x)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Total @ 15× Achievement
            </p>
            <p
              className="font-display text-2xl mt-1 text-emerald-700 font-mono-num"
              data-testid="s2-total-at-15x"
            >
              {formatINR(s2.total_at_15x)}
            </p>
          </div>
        </div>
      </article>
    </section>
  );
};

export default ScenarioSummary;
